import type { FullActivityItem } from '@customTypes/types';
import { PencilIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { ActionIcon, Avatar, AvatarGroup, Box, Divider, Flex, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { resolveImagePath } from '@/shared/helper';

import { EditTaskModal } from './EditTaskModal';

export const TaskCard = ({
  task,
  dragHandleProps,
  onClick,
  ...props
}: {
  task: FullActivityItem;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  onClick?: () => void;
}) => {
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <EditTaskModal opened={opened} task={task} onClose={close} />
      <Box
        {...props}
        style={{
          padding: '12px',
          borderRadius: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.6)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
        }}
        onClick={onClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.85)';
        }}
      >
        <Stack gap='xs'>
          {/* Drag Handle - Full Width at Top */}
          <Box
            {...dragHandleProps}
            style={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              marginTop: '-12px',
              marginLeft: '-12px',
              marginRight: '-12px',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              backgroundColor: 'rgba(195, 207, 226, 0.25)',
              transition: 'background-color 0.2s ease'
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(195, 207, 226, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(195, 207, 226, 0.25)';
            }}
          >
            <Bars3Icon style={{ width: '24px', height: '24px', color: '#8b95a8' }} />
          </Box>

          <Flex align='center' justify='space-between'>
            <AvatarGroup>
              <Avatar
                size={36}
                src={task.artistPhoto ? resolveImagePath(task.artistPhoto) : ''}
              />
              <Avatar
                size={36}
                src={task.clubPhoto ? resolveImagePath(task.clubPhoto) : ''}
              />
            </AvatarGroup>
            <ActionIcon
              color='gray'
              size='sm'
              variant='subtle'
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
            >
              <PencilIcon style={{ width: '16px', height: '16px' }} />
            </ActionIcon>
          </Flex>
          <Divider />
          <Stack gap={4}>
            <Text c='dimmed' fw={500} size='xs'>
              Artist
            </Text>
            <Text fw={600} lineClamp={1} size='sm'>
              {task.artistName}
            </Text>
          </Stack>
          <Stack gap={4}>
            <Text c='dimmed' fw={500} size='xs'>
              Club
            </Text>
            <Text fw={600} lineClamp={1} size='sm'>
              {task.clubName}
            </Text>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};
